import type {
  InsuredPersonStateCommandData,
  ScenarioVersion,
} from '@festina/life-general-types/life'
import {
  type ActionIdRegistry,
  assertNotEmpty,
  notEmpty,
  type Optional,
  typeUtils,
} from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { FormikForm, FormikSelect } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { withSuspense } from '@festina-life/ui/libs'
import { SimpleModal } from '@festina-life/ui/structure'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import * as Yup from 'yup'

import {
  useScenarioContext,
  type VariantIdentifier,
} from '../../context/scenarioContext'
import { useHook, useService } from '../../lenteHooks'
import type { translations } from '../../translations/translations'
import { ErrorMessage } from '../errorFallback/ErrorMessage'
import { scenarioStyles } from '../participantOverview/scenarios/scenarioStyles'
import { useSettingsContext } from '../settingsProvider/settingsContext'
import { LifeCommitmentSaveOrCreateScenarioFormListener } from './LifeCommitmentSaveOrCreateScenarioFormListener'

export type ScenarioFormProps = {
  scenarioIdOrTitle: string
  variantIdOrTitle: string
}

type Props = {
  actionId: keyof ActionIdRegistry
  isModalOpen: boolean
  onCloseModal: () => void
  onSubmitScenarioSuccess?: () => Promise<unknown>
}

const validateKey = typeUtils.createValidator<ScenarioFormProps>()

const LifeCommitmentSaveOrCreateScenarioModalManagerInternal = ({
  actionId,
  isModalOpen,
  onCloseModal,
  onSubmitScenarioSuccess,
}: Props) => {
  const translate = useTranslate()
  const { language } = useSettingsContext()

  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const { onChangeVariantIdentifier, variantIdentifier } = useScenarioContext()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submittedVariantIdentifier, setSubmittedVariantIdentifier] =
    useState<Optional<VariantIdentifier>>()

  const { data: commitmentVersion } = useHook(
    'fetchers.life.useFetchCommitmentVersionLatestNoSuspense',
  )(
    {
      ownerId,
      tenantId,
    },
    {
      retry: false,
    },
  )

  // As cast is necessary here as it would complain when running TSC through the terminal
  const actionMeta = useService('logic.actionMetadata') as ActionIdRegistry

  const specificActionMeta = typeUtils.hasField(actionMeta[actionId], 'title')
    ? (actionMeta[actionId].title as keyof (typeof translations)['en-GB'])
    : ''

  const { data: scenarioVersionCollection } = useHook(
    'fetchers.life.useFetchScenarios',
  )({
    tenantId,
    ownerId,
    cancelled: false,
    applied: false,
  })

  const { data: personVersion } = useHook(
    'fetchers.crm.useFetchPersonVersionLatest',
  )({
    tenantId,
    personId: ownerId,
  })

  const {
    mutateAsync: createScenarioTrigger,
    error: createScenarioError,
    reset: resetCreateScenarioError,
  } = useHook('mutators.life.useSubmitScenario')({
    tenantId,
    ownerId,
  })

  const {
    mutateAsync: createScenarioVariantTrigger,
    error: createScenarioVariantError,
    reset: resetCreateScenarioVariantError,
  } = useHook('mutators.life.useSubmitScenarioVariant')({
    tenantId,
    ownerId,
  })

  const handleOnClose = useCallback(() => {
    onCloseModal()
    resetCreateScenarioError()
    resetCreateScenarioVariantError()
    setSubmittedVariantIdentifier(undefined)
  }, [resetCreateScenarioError, resetCreateScenarioVariantError, onCloseModal])

  const { mutateAsync } = useHook('lazyFetchers.life.useLazyFetchScenarios')()

  const hasJustSubmitted = useRef(false)

  /**
   * We want to immediately submit the underlying form
   * Therefor when hasJustSubmitted and the context has been updated with the new variantIdentifier.
   * Note there is a test already at CommandModalButton.test that ensures that this works
   */
  useEffect(() => {
    if (!submittedVariantIdentifier || !variantIdentifier) {
      return
    }

    if (
      submittedVariantIdentifier.scenarioId !== variantIdentifier.scenarioId ||
      submittedVariantIdentifier.variantId !== variantIdentifier.variantId
    ) {
      return
    }

    const handleSuccess = async () => {
      if (!hasJustSubmitted.current) {
        return
      }
      hasJustSubmitted.current = false
      handleOnClose()
      await onSubmitScenarioSuccess?.()
    }

    void handleSuccess()
  }, [
    handleOnClose,
    onSubmitScenarioSuccess,
    submittedVariantIdentifier,
    variantIdentifier,
  ])

  const scenario = scenarioVersionCollection?.scenarioVersions
    ?.filter((x) => !x?.scenarioAppliedVersion)
    ?.map((x) => ({
      label: x?.scenarioTitle,
      id: x?._id,
    }))
    .sort((a, b) => (a?.label ?? '').localeCompare(b?.label ?? '', language))

  const handleCreateScenarioVariant = async ({
    scenarioId,
    variantId,
  }: {
    scenarioId: string
    variantId: string
  }) => {
    const commandData = {
      _type: 'InsuredPersonStateCommandData',
      person: {
        _type: 'PersonStateSpec',
        personId: ownerId,
        birthDate: personVersion?.birthDate,
      },
    } satisfies InsuredPersonStateCommandData

    const scenarioCreateCommitmentCommandVersions = {
      ...(commitmentVersion
        ? {}
        : {
            scenarioCreateCommitmentCommandVersions: [
              {
                _type: 'ScenarioCreateCommitmentCommandVersion',
                commandVersion: {
                  _type: 'CommandVersion',
                  commandId: crypto.randomUUID(),
                  commandType: 'pretendAddInsured',
                  commandData,
                },
                commandData,
                commandType: 'pretendAddInsured',
                ignoreCommandOnApply: true,
              },
            ],
          }),
    }

    return await createScenarioVariantTrigger({
      scenarioId,
      requestBody: {
        _type: 'ScenarioVariantVersion',
        variantTitle: variantId,
        ...scenarioCreateCommitmentCommandVersions,
      },
    })
  }

  const onSubmit = async ({
    scenarioIdOrTitle: scenarioId,
    variantIdOrTitle: variantId,
  }: ScenarioFormProps) => {
    let nextVariantIdentifier: Optional<VariantIdentifier>

    try {
      setIsSubmitting(true)
      const doesScenarioExist = scenario?.some(({ id }) => id === scenarioId)
      const doesVariantExist = scenarioVersionCollection?.scenarioVersions
        ?.find((x) => x?._id === scenarioId)
        ?.scenarioVariantVersions?.some(
          (variantVersion) => variantVersion?._id === variantId,
        )

      if (doesScenarioExist && doesVariantExist) {
        // Existing scenario and variant
        nextVariantIdentifier = {
          scenarioId,
          variantId,
        }
      } else if (doesScenarioExist && !doesVariantExist) {
        const variantIdOnScenario = await handleCreateScenarioVariant({
          scenarioId,
          variantId,
        })
        assertNotEmpty(variantIdOnScenario)
        nextVariantIdentifier = {
          scenarioId,
          variantId: variantIdOnScenario,
        }
      } else {
        // Create scenario, create variant and add command to it
        await createScenarioTrigger({
          requestBody: {
            _type: 'ScenarioVersion',
            scenarioTitle: scenarioId,
          },
        })

        //  I don't get the scenarioId back from above service so we need to fetch again
        const scenarioVersionCollection = await mutateAsync({
          ownerId,
          tenantId,
          cancelled: false,
          applied: false,
        })

        const scenarioIdFromVersion =
          scenarioVersionCollection?.scenarioVersions?.find(
            (x) => x?.scenarioTitle === scenarioId,
          )?._id

        assertNotEmpty(scenarioIdFromVersion)

        // Create variant on scenario
        const variantIdOnScenario = await handleCreateScenarioVariant({
          scenarioId: scenarioIdFromVersion,
          variantId,
        })
        assertNotEmpty(variantIdOnScenario)
        nextVariantIdentifier = {
          scenarioId: scenarioIdFromVersion,
          variantId: variantIdOnScenario,
        }
      }

      if (nextVariantIdentifier) {
        hasJustSubmitted.current = true
        setSubmittedVariantIdentifier(nextVariantIdentifier)
        onChangeVariantIdentifier(nextVariantIdentifier)
      }
    } finally {
      // The mutation loading state is not possible to use as there are gaps between and after the mutations, as we want to keep the loading state as stable through the entire promise.
      // Therefor we handle the loading state manually here, and set it to false in the finally block.
      setIsSubmitting(false)
    }
  }

  const yupSchema: Yup.ObjectSchema<ScenarioFormProps> = Yup.object({
    scenarioIdOrTitle: Yup.string().required(translate('validation.required')),
    variantIdOrTitle: Yup.string().required(translate('validation.required')),
  })

  const formId = useId()

  if (variantIdentifier && !submittedVariantIdentifier && !isModalOpen) {
    return null
  }

  return (
    <SimpleModal
      isOpen={isModalOpen}
      closeConfiguration={{
        onClose: handleOnClose,
      }}
      title={translate('commitmentScenario.modal.create')}
      acceptConfiguration={{
        formId,
        loading: isSubmitting,
        label: translate('commitmentScenario.modal.create'),
      }}
      style={scenarioStyles}
    >
      <FormikForm
        initialValues={{
          scenarioIdOrTitle: specificActionMeta
            ? (scenario?.find((x) => x.label === translate(specificActionMeta))
                ?.id ?? translate(specificActionMeta))
            : '',
          variantIdOrTitle: specificActionMeta
            ? translate(specificActionMeta)
            : '',
        }}
        onSubmit={onSubmit}
        validationSchema={yupSchema}
        formId={formId}
      >
        {({ values }) => (
          <>
            {(createScenarioError || createScenarioVariantError) && (
              <ErrorMessage
                error={createScenarioError || createScenarioVariantError}
              />
            )}
            <LifeCommitmentSaveOrCreateScenarioFormListener />
            <FormikSelect
              options={scenario?.map((x) => x.id) ?? []}
              renderFn={(renderId) => {
                const existingScenario = scenario?.find(
                  ({ id }) => id === renderId,
                )
                return existingScenario ? existingScenario.label : renderId
              }}
              label={translate('commitmentScenario.form.scenario')}
              name={validateKey('scenarioIdOrTitle')}
              isValueCreator
              valueFromString={(value) => value}
            />
            {isNewScenario(scenario, values.scenarioIdOrTitle) && (
              <Alert severity="info" className="mb-4">
                {translate('commitmentScenario.form.newFieldInfo')}
              </Alert>
            )}
            {values.scenarioIdOrTitle && (
              <FormikSelect
                {...getOptionsAndRenderFn(
                  scenarioVersionCollection?.scenarioVersions?.find(
                    (x) => x?._id === values.scenarioIdOrTitle,
                  ),
                )}
                label={translate('commitmentScenario.form.variant')}
                name={validateKey('variantIdOrTitle')}
                isValueCreator
                valueFromString={(value) => value}
              />
            )}
            {!isNewScenario(scenario, values.scenarioIdOrTitle) &&
              isNewVariant(
                scenarioVersionCollection?.scenarioVersions?.find(
                  (scenarioVersion) =>
                    scenarioVersion?._id === values.scenarioIdOrTitle,
                ),
                values.variantIdOrTitle,
              ) && (
                <Alert severity="info" className="mb-4">
                  {translate('commitmentScenario.form.newFieldInfo')}
                </Alert>
              )}
          </>
        )}
      </FormikForm>
    </SimpleModal>
  )
}

const getOptionsAndRenderFn = (scenarioVersions: Optional<ScenarioVersion>) => {
  const nonCancelledVariants = scenarioVersions?.scenarioVariantVersions
    ?.filter((x) => !x?.scenarioVariantCancellationVersion)
    .filter(notEmpty)

  const options =
    nonCancelledVariants?.map(({ _id }) => _id)?.filter(notEmpty) ?? []
  const renderFn = (renderId: string) =>
    nonCancelledVariants?.find(({ _id }) => _id === renderId)?.variantTitle ??
    renderId
  return { options, renderFn }
}

const isNewScenario = (
  scenario: Optional<{ id: Optional<string> }[]>,
  scenarioId: Optional<string>,
) => scenarioId && !scenario?.find(({ id }) => id === scenarioId)

const isNewVariant = (
  scenarioVersions: Optional<ScenarioVersion>,
  variantId: Optional<string>,
) =>
  variantId &&
  !scenarioVersions?.scenarioVariantVersions
    ?.filter(notEmpty)
    ?.some(({ _id }) => _id === variantId)

export const LifeCommitmentSaveOrCreateScenarioModalManager = withSuspense(
  // By the time the modal is opened, suspense should be finished.
  // So there is no need to suspense/spinner on the modal body.
  LifeCommitmentSaveOrCreateScenarioModalManagerInternal,
)
